import { skipToken, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { isOrpcForbiddenError, isOrpcNotFoundError } from '@/_shared/lib/orpc-errors';
import type { SSEMessage } from '@/_shared/types';

import { isTurnTerminalInHistory } from '../lib/turn-slicing';

const MAX_IDLE_READS = 5;
const MAX_FAILED_READS = 5;
const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 30_000;

interface HistorySnapshot {
  session: { id: string };
  sseMessages: SSEMessage[];
  runtime: { activeTurn: { turnId: string } | null; recoveryAbort?: SSEMessage | null };
}

interface EndedStreamHistoryOptions {
  sessionId: string | null;
  turnId: string | null;
  ended: boolean;
  recoverHistory: (history: SSEMessage[], turnId: string, abortMarker?: SSEMessage | null) => void;
}

interface PollAccounting {
  key: string;
  reads: number;
  idleReads: number;
  failedReads: number;
  accountedDataCount: number;
  accountedErrorCount: number;
  /** False once a read says there is nothing further to wait for. */
  continue: boolean;
}

function isAssistCard(message: SSEMessage): boolean {
  return message.type === 'ui_message' && message.uiType === 'assist_suggestion';
}

function historyThroughTurn(messages: SSEMessage[], turnId: string): SSEMessage[] {
  const terminalIndex = messages.findIndex(
    (message) =>
      (message.type === 'turn_complete' || message.type === 'turn_aborted' || message.type === 'error') &&
      message.turnId === turnId,
  );
  if (terminalIndex >= 0) {
    // In-chat assist writes its card after the terminal row; keep the turn's cards that follow it.
    let end = terminalIndex + 1;
    while (end < messages.length && isAssistCard(messages[end]!)) end += 1;
    return messages.slice(0, end);
  }

  const startIndex = messages.findIndex((message) => message.type === 'turn_start' && message.turnId === turnId);
  if (startIndex < 0) return messages;
  const nextStartIndex = messages.findIndex(
    (message, index) => index > startIndex && message.type === 'turn_start' && message.turnId !== turnId,
  );
  return nextStartIndex >= 0 ? messages.slice(0, nextStartIndex) : messages;
}

function freshPoll(key: string): PollAccounting {
  return {
    key,
    reads: 0,
    idleReads: 0,
    failedReads: 0,
    accountedDataCount: 0,
    accountedErrorCount: 0,
    continue: false,
  };
}

/**
 * Fold one settled read into the poll counters. `refetchInterval` can run more than once for the
 * same result, so each data or error update is counted once.
 */
function accountPoll(
  state: {
    data?: HistorySnapshot;
    error: unknown;
    dataUpdateCount: number;
    errorUpdateCount: number;
  },
  poll: PollAccounting,
  sessionId: string,
  turnId: string,
): void {
  if (state.data && state.dataUpdateCount > poll.accountedDataCount) {
    poll.accountedDataCount = state.dataUpdateCount;
    poll.failedReads = 0;
    poll.continue = false;
    if (state.data.session.id !== sessionId) return;
    const history = historyThroughTurn(state.data.sseMessages, turnId);
    const abortMarker =
      state.data.runtime.recoveryAbort?.type === 'turn_aborted' ? state.data.runtime.recoveryAbort : null;
    if (abortMarker || isTurnTerminalInHistory(history, turnId)) return;
    if (state.data.runtime.activeTurn?.turnId && state.data.runtime.activeTurn.turnId !== turnId) return;
    // A terminal row with no saved terminal event cannot produce a complete transcript. Keep
    // the stream warning, but stop polling after a few chances for that event to appear.
    poll.idleReads = state.data.runtime.activeTurn ? 0 : poll.idleReads + 1;
    if (poll.idleReads >= MAX_IDLE_READS) return;
    poll.reads += 1;
    poll.continue = true;
    return;
  }

  if (state.error && state.errorUpdateCount > poll.accountedErrorCount) {
    poll.accountedErrorCount = state.errorUpdateCount;
    poll.continue = false;
    if (isOrpcForbiddenError(state.error) || isOrpcNotFoundError(state.error)) return;
    poll.failedReads += 1;
    if (poll.failedReads >= MAX_FAILED_READS) return;
    poll.reads += 1;
    poll.continue = true;
  }
}

function pollDelay(poll: PollAccounting): number | false {
  if (!poll.continue) return false;
  return Math.min(BASE_DELAY_MS * 2 ** Math.min(poll.reads - 1, 4), MAX_DELAY_MS);
}

/**
 * The counters for `key`, resetting when the session or turn changes.
 *
 * The reset lives here, not in the render body: a new key has to be a fresh poll before the next
 * interval decision, and reading or writing a ref during render is what `react-hooks/refs` forbids.
 * Every caller is a query callback, which runs after render.
 */
function pollForKey(pollRef: { current: PollAccounting }, key: string): PollAccounting {
  if (pollRef.current.key !== key) pollRef.current = freshPoll(key);
  return pollRef.current;
}

/** Restore the saved transcript when SSE has permanently ended without a usable terminal event. */
export function useEndedStreamHistory({ sessionId, turnId, ended, recoverHistory }: EndedStreamHistoryOptions): void {
  const orpc = useOrpc();
  const recoverHistoryRef = useRef(recoverHistory);
  useEffect(() => {
    recoverHistoryRef.current = recoverHistory;
  }, [recoverHistory]);

  const pollKey = ended && sessionId && turnId ? `${sessionId}:${turnId}` : '';
  const pollRef = useRef<PollAccounting>(freshPoll(pollKey));

  const { data } = useQuery({
    ...orpc.chat.getRecovery.queryOptions({
      input:
        ended && sessionId && turnId ? { id: sessionId, includeActiveEvents: true, recoveryTurnId: turnId } : skipToken,
    }),
    retry: false,
    // The app default keeps reads fresh for 30s. Recovery has to see each poll's new events.
    staleTime: 0,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: () => pollForKey(pollRef, pollKey).continue,
    refetchOnReconnect: () => pollForKey(pollRef, pollKey).continue,
    refetchInterval: (query) => {
      if (!sessionId || !turnId) return false;
      const poll = pollForKey(pollRef, pollKey);
      accountPoll(query.state, poll, sessionId, turnId);
      return pollDelay(poll);
    },
  });

  useEffect(() => {
    if (!ended || !sessionId || !turnId || !data || data.session.id !== sessionId) return;
    const history = historyThroughTurn(data.sseMessages, turnId);
    const abortMarker = data.runtime.recoveryAbort?.type === 'turn_aborted' ? data.runtime.recoveryAbort : null;
    recoverHistoryRef.current(history, turnId, abortMarker);
  }, [data, ended, sessionId, turnId]);
}
